<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <v-btn
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>{{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <validation-observer ref="observerRef">
        <settings-group
          :title="$t('title.reserve_with_google')"
          :subtitle="$t('settings.online.reserve_with_google_subtitle')"
          gutter
        >
          <v-col
            cols="12"
            class="pt-2 pb-3"
          >
            <v-switch
              v-model="a.reserveWithGoogle"
              class="pa-0 ma-0 pb-2"
              color="primary"
              :label="$i18n.t('title.reserve_with_google')"
              hide-details
            />
            {{ $t('label.description_online_reserve_with_google') }}
          </v-col>
        </settings-group>

        <settings-group
          :title="$t('label.online_reservation_options')"
          :subtitle="$t('settings.online.online_options_subtitle')"
          gutter
        >
          <v-col cols="6">
            <v-select
              v-model="a.defaultMinPartySize"
              :items="partysizes()"
              :label="$t('label.min_party_size')"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="a.defaultMaxPartySize"
              :items="partysizes()"
              :label="$t('label.max_party_size')"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="a.minMinutesInAdvance"
              :items="useTimeInAdvanceItems().value"
              :label="$t('label.min_minutes_prior_to_arrival_time')"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="a.maxDaysInAdvance"
              :items="useDaysInAdvanceItems()"
              :label="$t('label.max_days_in_advance')"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="a.minMinutesToModify"
              :label="$t('label.modify_cancel_in_advance')"
              :items="useTimeInAdvanceItems().value"
            />
          </v-col>
          <v-col cols="6">
            <time-picker-field
              v-model="a.maxTimeForToday"
              :label="$t('label.close_availability_for_today_at_specific_time')"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="a.defaultMaxGuestsPerTime"
              :label="$t('label.max_guests_per_time')"
              :items="maxGuestsItems"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="a.defaultMaxReservationsPerTime"
              :label="$t('label.max_reservations_per_time')"
              :items="maxReservationsItems"
            />
          </v-col>
          <v-col
            cols="12"
            class="pb-6"
          >
            <v-switch
              v-model="a.enableReconfirmation"
              :label="$t('label.enable_reconfirm')"
              class="pa-0 ma-0 pb-2"
              hide-details
            />
            {{ $t('label.description_widget_reconfirm') }}
          </v-col>
          <v-col cols="12">
            <paywall-switch
              v-model="a.enableWaitlist"
              :unlocked="isWaitlistFeatureAllowed"
              :label="$t('label.wait_list_enable')"
              class="pa-0 ma-0 pb-2"
              hide-details
            />
            {{ $t('label.description_widget_waitlist') }}
          </v-col>
        </settings-group>
      </validation-observer>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performSaveAction } from '@/services/vue-utils';
import AccountSettings from '@/model/AccountSettings';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import { ValidationObserver } from 'vee-validate';
import useUnsavedGuard from '@/composables/unsaved-guard';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import useStoreWatch from '@/composables/store-watch';
import PaywallSwitch from '@/components/buttons/PaywallSwitch.vue';

import { partysizes, useDaysInAdvanceItems, useTimeInAdvanceItems } from './settings/helpers/settings-values';

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const a = ref(new AccountSettings());
const olda = ref(new AccountSettings());

const isWaitlistFeatureAllowed = computed<boolean>(() => store.getters.isWaitlistFeatureAllowed);

// Unsaved Guard
const isChanged = computed(() => !isModelEqualToModel(a.value, olda.value));
useUnsavedGuard(isChanged);

const maxOnlineCapacity = computed(() => store.getters.maxOnlineCapacity);

const maxGuestsItems = computed(() => [...new Array(501)].map((e, i) => ({
  text: i === 0 ? i18n.t('label.maximum_x', [maxOnlineCapacity.value]) : `${i}`,
  value: i,
})));

const maxReservationsItems = computed(() => [...new Array(501)].map((e, i) => ({
  text: i === 0 ? i18n.t('label.maximum') : `${i}`,
  value: i,
})));

// store watch, load data
const reset = async () => {
  const as = store.state.settings.accountSettings;
  a.value = cloneModel(as);
  olda.value = as;
};

useStoreWatch(reset, true);

const save = async () => {
  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendAccountSettings', { entity: a.value }),
  );

  if (ok) reset();
};

</script>
